/**
 * Host-bridge context (crucible#2326).
 *
 * A thin, optional seam letting the shared Workspace ask the Electron HOST
 * (the cloud desktop shell) to perform a capability the preload-less cookie-
 * mode SPA cannot reach in-process — today: open the NATIVE ProtoHub simulator
 * focused on a game, at the cloud/staging origin.
 *
 * Provided ONLY by the cloud desktop shell (`crucible-web` cloud-root, desktop
 * branch). It is ABSENT in:
 *   - local Electron (the renderer can open the native simulator directly via
 *     its preload — `hasNativeSimulator: true`), and
 *   - the pure-web SPA (no Electron host exists to service the request).
 * Consumers MUST treat a `null` bridge as "no host capability — fall back to
 * the in-process path or no-op". See {@link useHostBridge}.
 */
import { createContext, useContext, type ReactNode } from 'react';

export interface HostBridge {
  /**
   * Ask the Electron host to open the native ProtoHub simulator focused on
   * `gameId` (cloud/staging origin). Fire-and-forget: the host owns the window
   * lifecycle. Implemented in the cloud desktop shell by navigating to a
   * sentinel path the Electron main process intercepts (mirrors the
   * switch-to-local sentinel). `gameId` is re-validated host-side before use.
   */
  requestProtoHubPreview(gameId: string): void;
}

const HostBridgeContext = createContext<HostBridge | null>(null);

export function HostBridgeProvider({
  value,
  children,
}: {
  value: HostBridge | null;
  children: ReactNode;
}): React.JSX.Element {
  return <HostBridgeContext.Provider value={value}>{children}</HostBridgeContext.Provider>;
}

/**
 * The active host bridge, or `null` when none is mounted (local Electron /
 * pure-web). Non-throwing by design — most surfaces render without a bridge.
 */
export function useHostBridge(): HostBridge | null {
  return useContext(HostBridgeContext);
}
